import { queryOptions } from '@tanstack/react-query'

import { searchFirstOrderPage, searchRemainingOrders } from './ordersApi'

import type { Environment } from '../App'
import type { QueryClient } from '@tanstack/react-query'

/**
 * Everything needed to address one customer's order history in a query cache.
 *
 * Exported because the Customer Hub warms the first page from its overview tab,
 * before this package has mounted anything. The key and the fetcher have to be
 * written once: a prefetch spelled separately seeds a cache entry the list
 * never reads, and the warm-up silently becomes a wasted request.
 */
export interface OrdersQueryParams {
  accountNumber: string
  env: Environment
  authToken?: string
}

/**
 * Pinned here rather than left to the host. The Customer Hub mounts this
 * package under a bare `new QueryClient()` (staleTime 0, retry 3,
 * refetchOnWindowFocus), and each of these queries can cost several requests —
 * so a tab switch or one flaky page would otherwise trigger a whole new round.
 *
 * `staleTime` is also what makes the overview prefetch worth doing: the list
 * mounts inside this window and paints from cache instead of refetching.
 */
const sharedQueryOptions = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
} as const

/**
 * The page the list paints with — one request, and the only one that blocks.
 *
 * Deliberately does NOT take the abort signal. React Query aborts an in-flight
 * fetch the moment a query's observer count drops to zero, but only when the
 * query function has *read* `signal` — reading it flips an internal
 * `abortSignalConsumed` flag, and `removeObserver` then calls
 * `retryer.cancel()` instead of the harmless `cancelRetry()`.
 *
 * The prefetch starts with no observers at all, so the first observer that
 * mounts and unmounts kills it: StrictMode's double-mount in dev, and a real
 * remount when `handleTabClick` resets the selected order and
 * `OrderListContainer` swaps views. The warm-up would be cancelled by the very
 * click it exists to speed up.
 *
 * Not passing it costs nothing worth having: this is one 20-order request whose
 * whole purpose is to reach the cache, so letting it finish after a navigation
 * is the outcome we want anyway.
 */
export function firstOrderPageQuery({
  accountNumber,
  env,
  authToken,
}: OrdersQueryParams) {
  return queryOptions({
    queryKey: ['orders', accountNumber, env, 'first'] as const,
    queryFn: () => searchFirstOrderPage({ accountNumber }, env, authToken),
    ...sharedQueryOptions,
  })
}

/**
 * The background walk. Deliberately not prefetched: it is the expensive half,
 * and a customer who never opens the Orders tab should never pay for it.
 *
 * This one *does* take the signal, so leaving the tab abandons it — the
 * opposite call to the first page above, and for the opposite reason. It is
 * several requests deep and nothing renders from it, so a customer who
 * navigates away should stop paying for it immediately.
 */
export function remainingOrdersQuery({
  accountNumber,
  env,
  authToken,
}: OrdersQueryParams) {
  return queryOptions({
    queryKey: ['orders', accountNumber, env, 'rest'] as const,
    queryFn: ({ signal }) =>
      searchRemainingOrders({ accountNumber }, env, authToken, signal),
    ...sharedQueryOptions,
  })
}

/**
 * Warm the first page into the host's cache before the Orders tab is opened,
 * so the list paints from cache instead of starting a round trip on mount.
 *
 * First page only, by design — the tail still waits for the tab to become
 * active, so a customer who never opens Orders costs one small request rather
 * than the whole walk. Resolves rather than rejects on failure, and is a no-op
 * while a fresh entry is already cached, so it is safe to call on every render
 * of the overview.
 */
export function prefetchFirstOrderPage(
  queryClient: QueryClient,
  params: OrdersQueryParams,
): Promise<void> {
  return queryClient.prefetchQuery(firstOrderPageQuery(params))
}
